import { autoinject, LogManager } from 'aurelia-framework';
import { EntityManager } from 'aurelia-orm';
import { TenantRepository } from "data/service/tenant-repository";
import { AppUserInfo, Tenant } from "data/entities/entities";
import { DirectoryService } from "data/service/directory-service";
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AppEvents } from 'core/app-events';
import { DirectoryInfo } from 'data/entities/directory-info';
import { ValidationController } from 'aurelia-validation';
import { BootstrapFormRenderer } from 'core/bootstrap-form-renderer';
import { DialogService } from 'aurelia-dialog';
import { Prompt } from 'shared/components/prompt/prompt';
import { AppRepository } from 'data/app-repository';
import { Alert } from 'shared/components/alert/alert';

@autoinject()
export class TeamViewModel {
	eventSubscriptions: Array<Subscription> = [];
	appUserInfoRepository: AppRepository;
	tenantRepository: TenantRepository;
	items: Array<AppUserInfo>;
	logger = LogManager.getLogger("TeamViewModel");
	tenant: Tenant;
	isEditing = false;
	teams: Array<DirectoryInfo>;

	tooltipcontent = "Hello Tooltip <button click.delegate='hide(1)' ej-button='e-text: Got It'></button>";
	tooltiptitle = 'Hello Title';
	//animationSettings = { effect: "slide", speed: 300 };

	hide(id) {
		this.logger.info('info-egg was tagged');
	}

	constructor(private entityManager: EntityManager,
		bootstrapFormRenderer: BootstrapFormRenderer,
		private dialogService: DialogService,
		//private accountService: AccountService,
		private eventAggregator: EventAggregator,
		private validationController: ValidationController,
		private directoryService: DirectoryService) {
		this.validationController.addRenderer(bootstrapFormRenderer);
		this.appUserInfoRepository = this.entityManager.getRepository(AppUserInfo);
		this.tenantRepository = this.entityManager.getRepository(Tenant);
		this.eventSubscriptions._add(this.eventAggregator.subscribe(AppEvents.ITEM_NAME_CHANGED, payload => {
			this.getExistingTenants();
		}));
	}

	confirmDeactivateTeamMember(id) {
		const appUser = this.items._findObjectById(id);
		const options = {
			message: "Are you sure you want to deactivate this team member?",
			content: appUser.fullName,
			content2: appUser.email,
			title: "Confirm",
			additionalInfo: "They will no longer have access to this team"
		};

		this.dialogService.open({ viewModel: Prompt, model: options }).whenClosed(response => {
			if (!response.wasCancelled) {
				this.deactivateTeamMember(id);
			}
		});
	}

	confirmRemoveOwnerRole(id) {
		const appUser = this.items._findObjectById(id);
		const options = {
			message: "Are you sure you want to remove the Owner role for this team member?",
			content: appUser.fullName,
			content2: appUser.email,
			title: "Confirm",
			additionalInfo: "They will no longer be able to view or edit billing information"
		};

		this.dialogService.open({ viewModel: Prompt, model: options }).whenClosed(response => {
			if (!response.wasCancelled) {
				this.removeOwnerRole(id);
			}
		});
	}

	confirmRemoveAdminRole(id) {
		const appUser = this.items._findObjectById(id);
		const options = {
			message: "Are you sure you want to remove the Administration role for this team member?",
			content: appUser.fullName,
			content2: appUser.email,
			title: "Confirm",
			additionalInfo: "They will no longer be able to add or remove Team Members"
		};

		this.dialogService.open({ viewModel: Prompt, model: options }).whenClosed(response => {
			if (!response.wasCancelled) {
				this.removeAdminRole(id);
			}
		});
	}

	confirmActivateTeamMember(id) {
		const appUser = this.items._findObjectById(id);
		const options = {
			message: "Are you sure you want to restore this team member?",
			content: appUser.fullName,
			content2: appUser.email,
			title: "Confirm"
		};

		this.dialogService.open({ viewModel: Prompt, model: options }).whenClosed(response => {
			if (!response.wasCancelled) {
				this.activateTeamMember(id);
			}
		});
	}

	NotifyTeamIsChanged(teamName: string) {
		const message = `You are now viewing the <b> ${teamName} </b>team details.`;
		const options = {
			message: message
		};
		this.dialogService.open({ viewModel: Alert, model: options });
	}

	activateTeamMember(id) {
		const appUserInfo: AppUserInfo = this.items._findObjectById(id);
		appUserInfo.isActive = true;
		this.appUserInfoRepository.call('activate', id);
	}

	async addAdminRole(id) {
		try {
			const result = await this.appUserInfoRepository.call('addAdminRole', id);
			if (result.hasError)
				this.showAlert(result.message);
			else {
				const appUserInfo: AppUserInfo = this.items._findObjectById(id);
				appUserInfo.isAdmin = true;
			}
		}
		catch (error) {
			alert(error);
		}
	}

	async addOwnerRole(id) {
		try {
			const result = await this.appUserInfoRepository.call('addOwnerRole', id);
			if (result.hasError)
				this.showAlert(result.message);
			else {
				const appUserInfo: AppUserInfo = this.items._findObjectById(id);
				appUserInfo.isOwner = true;
			}
		}
		catch (error) {
			alert(error);
		}
	}

	async removeOwnerRole(id) {
		try {
			const result = await this.appUserInfoRepository.call('removeOwnerRole', id);
			if (result.hasError)
				this.showAlert(result.message);
			else {
				const appUserInfo: AppUserInfo = this.items._findObjectById(id);
				appUserInfo.isOwner = false;
			}
		}
		catch (error) {
			alert(error);
		}
	}

	async removeAdminRole(id) {
		try {
			const result = await this.appUserInfoRepository.call('removeAdminRole', id);
			if (result.hasError)
				this.showAlert(result.message);
			else {
				const appUserInfo: AppUserInfo = this.items._findObjectById(id);
				appUserInfo.isAdmin = false;
			}
		}
		catch (error) {
			alert(error);
		}
	}

	async deactivateTeamMember(id) {
		try {
			const result = await this.appUserInfoRepository.call('deactivate', id);
			if (result.hasError)
				this.showAlert(result.message);
			else {
				const appUserInfo: AppUserInfo = this.items._findObjectById(id);
				appUserInfo.isActive = false;
			}
		}
		catch (error) {
			alert(error);
		}
	}

	showAlert(message: any): any {
		const options = { message: message };
		this.dialogService.open({ viewModel: Alert, model: options });
	}

	async validate() {
		const result = await this.validationController.validate();
		if (result.valid) {
			return true;
		} else {
			return false;
		}
	}

	async getExistingTenants() {
		try {
			this.teams = await this.directoryService.listAsync();
		}
		catch (error) {
			this.logger.error(error);
		}
	}

	goToTeam(teamId, teamName: string) {
		this.logger.info("Navigate to TeamId of: " + teamId);
		this.eventAggregator.publish(AppEvents.CHANGE_DIRECTORY, teamId);
		this.NotifyTeamIsChanged(teamName);
	}

	editTeam() {
		this.isEditing = true;
	}

	cancelEdit() {
		this.isEditing = false;
		this.tenant.reset();
	}

	async saveEdit() {
		this.logger.debug('saveEdit()');
		const result = await this.validationController.validate();
		if (result.valid) {
			await this.tenant.save();;
			this.isEditing = false;
			setTimeout(() => {
				this.eventAggregator.publish(AppEvents.ITEM_NAME_CHANGED, this.tenant);
			}, 50)
		}
		else {
			console.log("failure");
		}
	}

	activate() {
		this.eventAggregator.publish(AppEvents.ToggleIsBusyVisiblity, true);
		this.getTeamMembers();
		this.getTeamName();
		this.getExistingTenants();
		this.eventAggregator.publish(AppEvents.ToggleIsBusyVisiblity, false);
	}

	async getTeamName() {
		try {
			this.tenant = await this.tenantRepository.find();
		}
		catch (exception) {
			this.logger.error(exception);
		}
	}

	async getTeamMembers() {
		try {
			this.items = await this.appUserInfoRepository.find();
		}
		catch (exception) {
			this.logger.error(exception);
		}
	}

	detached() {
		this.logger.info("detached");
		this.unsubscribeFromEvents();
	}

	private unsubscribeFromEvents() {
		for (const entry of this.eventSubscriptions) {
			entry.dispose();
		}
		this.eventSubscriptions = [];
	}
}